import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  Box,
  Heading,
  IconChart,
  Skeleton,
  Stack,
  Text,
  TextDropdown
} from '@machobear/design-system'
import { useParams } from 'react-router'
import { Suspense, useState } from 'react'
import useMediaQuery from '@/hooks/use-media-query'
import DashboardHeader from '@/features/Listing/dashboard-header'
import { breakpoints } from '@/styles'
import InsightsCard from '@/features/SponsoredListing/insights-card'
import { InsightsChart } from '@/components/insights-chart'
import { useFlag } from '@/flags'
import PageTitle from '@/components/page-title'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-top: 24px;
  gap: 24px;
  width: 100%;
  max-width: 850px;
  padding: 0 50px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: center;
    padding: 0 20px;
  }
`

const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  text-align: left;
`

type Params = {
  listingId: string
}

const ListingInsights = () => {
  const { t } = useTranslation()
  const { listingId } = useParams<Params>()
  const hideHeader = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const [period, setPeriod] = useState<'7d' | '30d' | '30dp'>('7d')
  const renderNewInsights = useFlag('features.listing.insights', false)

  return (
    <>
      <PageTitle>{t('l.listing.view.insights.title', 'Insights')}</PageTitle>
      <Helmet>
        <title>{t('l.listing.dashboard.insights.title', 'Insights')}</title>
      </Helmet>
      {!hideHeader && (
        <DashboardHeader
          listingId={listingId}
          isPublished={null}
          showButtons={null}
          border={null}
          currentPage={t('l.listing.dashboard.insights.title', 'Insights')}
          height={55}
        />
      )}
      <Wrapper>
        <Title>{t('l.listing.dashboard.insights.title', 'Insights')}</Title>
        <Stack space="medium">
          {renderNewInsights && (
            <Box background="neutralLight" borderRadius="standard" padding="medium" width="full">
              <Stack space="medium">
                <Box width="full" display="flex" justifyContent="spaceBetween">
                  <Heading level="6" icon={<IconChart />} weight="regular">
                    {t('l.listing.dashboard.views.title', 'Views')}
                  </Heading>
                  <Text>
                    <TextDropdown
                      id="period"
                      label={t('l.listing.dashboard.insights.period', 'Period')}
                      options={[
                        {
                          value: '7d',
                          text: t('l.listing.dashboard.insights.last_7_days', 'Last 7 days')
                        },
                        {
                          value: '30d',
                          text: t('l.listing.dashboard.insights.last_30_days', 'Last 30 days')
                        },
                        {
                          value: '30dp',
                          text: t(
                            'l.listing.dashboard.insights.previous_30_days',
                            'Previous 30 days'
                          )
                        }
                      ]}
                      onChange={value => setPeriod(value as typeof period)}
                      value={period}
                    />
                  </Text>
                </Box>
                <Box width="full" style={{ height: '300px' }}>
                  <Suspense fallback={<Skeleton.Shape width="full" height={300} />}>
                    <InsightsChart listingId={listingId} period={period} />
                  </Suspense>
                </Box>
              </Stack>
            </Box>
          )}
          <InsightsCard listingId={listingId} />
        </Stack>
      </Wrapper>
    </>
  )
}

export default ListingInsights
